import React, { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import t from '@jetshop/intl';
import { styled } from 'linaria/react';
import { Button, TrendLink } from '../ui/Button';
import { useLocation } from 'react-router';
import qs from 'qs';
import { CartIdContext } from '@jetshop/core/components/Cart/CartIdContext';
import addMultipleToCartMutation from '../Cart/addMultipleToCart.gql';
import { addToCartError } from '@jetshop/core/components/Mutation/AddToCart/addToCartUtils';
import MaxWidth from '../Layout/MaxWidth';
import Spinner from '../ui/Spinner';
import CartProvider from '@jetshop/core/components/Query/CartProvider';
import cartQuery from '../Cart/CartQuery.gql';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { trackCartCheckout } from '@jetshop/core/analytics/tracking';
import { useTracker } from '@jetshop/core/analytics/Analytics';
import ItemList from './ItemList';
import { ReactComponent as Check } from '@jetshop/ui/svg/Check.svg';
import { theme } from '../Theme';

const Container = styled(MaxWidth)`
  padding-top: 4rem !important;
  padding-bottom: 4rem !important;

  h2 {
    font-size: 1.25rem;
    display: flex;
    align-items: center;

    svg {
      width: 2rem;
      height: 2rem;
      margin: 0 0.5rem 0 0;

      use {
        fill: #393;
      }
    }
  }

  .loading-msg {
    display: flex;
    align-items: center;
  }

  .error-icon {
    display: inline-flex;
    width: 1.75rem;
    height: 1.75rem;
    border: 2px solid ${theme.colors.darkRed};
    font-size: 1.25rem;
    text-align: center;
    border-radius: 100%;
    margin: 0 1rem 0 0;
    color: ${theme.colors.darkRed};
    font-weight: bold;
    justify-content: center;
    align-items: center;
  }

  .error-msg h2 {
    font-size: 1.125rem;
    color: ${theme.colors.darkRed};
  }

  .buttons {
    display: flex;

    > *:first-child {
      margin-right: 1rem;
    }
  }
`;

const CheckoutButton = styled(Button)``;

const ContinueButton = styled(TrendLink)`
  background: ${theme.colors.notWhite};
  color: ${theme.colors.black};

  &:hover {
    color: ${theme.colors.notWhite};
    background: ${theme.colors.black};
  }
`;

const LoadingMsg = () => (
  <div className="loading-msg">
    <Spinner />
    <h2 style={{ marginLeft: '0.5rem' }}>{t('Adding products to cart')}</h2>
  </div>
);

const ErrorMsg = ({ msg, msg2 }) => (
  <div className="error-msg">
    <h2>
      <span className="error-icon">!</span> {t(msg)}
    </h2>
    {msg2 && <p>{t(msg2)}</p>}
  </div>
);

const SuccessMsg = () => {
  return (
    <div className="success">
      <h2>
        <Check />
        <span>{t('Your products were added to the cart')}</span>
      </h2>
    </div>
  );
};

const CartContents = () => {
  const { selectedChannel } = useContext(ChannelContext);
  const track = useTracker();

  return (
    <CartProvider query={cartQuery}>
      {(cart) => {
        return cart.data && cart.data.cart ? (
          <div className="cart-content">
            <ItemList cartData={cart.data.cart} />

            <div className="buttons">
              <CheckoutButton
                anchor={true}
                href={`${cart.data.cart.externalCheckoutUrl}&channelCountry=${selectedChannel.country.code}`}
                onClick={() => {
                  track(trackCartCheckout({ cart }));
                }}
              >
                {t('To checkout')}
              </CheckoutButton>
              <ContinueButton to={'/'}>{t('Continue shopping')}</ContinueButton>
            </div>
          </div>
        ) : null;
      }}
    </CartProvider>
  );
};

const AddProducts = ({ productsToAdd, hasAdded, setHasAdded }) => {
  const [wasSuccess, setWasSuccess] = useState(false);
  const [error, setError] = useState(null);
  const { cartId, setCartId } = useContext(CartIdContext);
  const [useAddMultipleToCartMutation] = useMutation(addMultipleToCartMutation);
  const callbackOptions = {};

  const onAddToCartError = (callbackOptions) => {
    setError('Something went wrong adding products to cart. Please try again.');
  };

  const onAddToCartSuccess = (callbackOptions) => {
    setWasSuccess(true);
  };

  const AddToCart = () => {
    return useAddMultipleToCartMutation({
      variables: {
        cartId: cartId,
        items: productsToAdd,
      },
    })
      .then(
        addToCartSuccess({
          onAddToCartSuccess,
          callbackOptions,
          cartId,
          setCartId,
        })
      )
      .catch(addToCartError({ onAddToCartError, callbackOptions }));
  };

  useEffect(() => {
    if (!hasAdded) {
      setHasAdded(true);
      AddToCart();
    }
  });
  return (
    <>
      {error ? (
        <ErrorMsg msg={error} msg2={'Please double check your cart'}></ErrorMsg>
      ) : (
        <>{wasSuccess ? <SuccessMsg /> : <LoadingMsg />}</>
      )}
      {hasAdded && <CartContents />}
    </>
  );
};

const addToCartSuccess = function ({
  onAddToCartSuccess,
  callbackOptions,
  cartId,
  setCartId,
}) {
  return ({ data }) => {
    if (!cartId || data.addMultipleToCart.cart.id !== cartId) {
      data.addMultipleToCart && setCartId(data.addMultipleToCart.cart.id);
    }
    if (onAddToCartSuccess) {
      onAddToCartSuccess(callbackOptions);
    }
  };
};

const ProductAdder = () => {
  const location = useLocation();
  const [hasAdded, setHasAdded] = useState(false);
  const prodParamStr = qs.parse(location.search, { ignoreQueryPrefix: true })[
    'products'
  ];
  let productsToAdd = [];

  // CHECK FOR VALID PARAMS
  if (!prodParamStr || typeof prodParamStr !== 'string') {
    return <ErrorMsg msg={'No products parameter string'} />;
  }

  const paramArray = prodParamStr.split(',');

  // CHECK FOR VALID CONTENT
  if ((paramArray && paramArray.length < 2) || paramArray.length % 2) {
    return <ErrorMsg msg={'Invalid products parameter string'} />;
  }

  // ADD PRODUCTS TO OBJECT
  for (let i = 0; i < paramArray.length / 2; i++) {
    const articleNumber = paramArray[2 * i];
    const quantity = parseInt(paramArray[2 * i + 1]);
    if (!articleNumber || !quantity) {
      console.error(
        'Error in parameter array at product position ' +
          i +
          '. (No art.no/no qty)'
      );
      return <ErrorMsg msg={'Invalid products parameter string'} />;
    } else {
      productsToAdd.push({
        articleNumber: articleNumber,
        quantity: quantity,
      });
    }
  }

  return (
    <AddProducts
      productsToAdd={productsToAdd}
      hasAdded={hasAdded}
      setHasAdded={setHasAdded}
    />
  );
};

const AddProductsPage = () => (
  <Container>
    <ProductAdder />
  </Container>
);

export default AddProductsPage;
